/* eslint-disable import/no-default-export */
import API from 'app/gamma/src/api';
import { normalizeAction } from 'app/gamma/src/api/normalizers/action';
import {
  ActionModel,
  ActionResponse,
  StandardThunkAction,
} from 'app/gamma/src/types';
import { Action, actionCreator } from 'app/gamma/src/util/actions';
import { createReducer } from 'app/gamma/src/util/reducers';

import { Noun, Verb, trackUe } from '@trello/analytics';

// Action types
import {
  LOAD_ACTION_SUCCESS,
  LOAD_BOARD_SUCCESS,
  LOAD_MEMBER_ACTIVITY_SUCCESS,
  LoadActionSuccessAction,
} from 'app/gamma/src/modules/loaders';

const ADD_CARD_COMMENT = Symbol('models/ADD_CARD_COMMENT');
const DELETE_CARD_COMMENT = Symbol('models/DELETE_CARD_COMMENT');
const SET_COMMENT_TEXT = Symbol('models/SET_COMMENT_TEXT');

type AddCardCommentAction = Action<typeof ADD_CARD_COMMENT, ActionResponse>;
type DeleteCardCommentAction = Action<typeof DELETE_CARD_COMMENT, string>;
type SetCommentTextAction = Action<typeof SET_COMMENT_TEXT, ActionResponse>;

const addCardCommentAction = actionCreator<AddCardCommentAction>(
  ADD_CARD_COMMENT,
);
const deleteCardCommentAction = actionCreator<DeleteCardCommentAction>(
  DELETE_CARD_COMMENT,
);
const setCommentTextAction = actionCreator<SetCommentTextAction>(
  SET_COMMENT_TEXT,
);

export type ActionsState = ActionModel[];
const initialState: ActionsState = [];

export default createReducer(initialState, {
  [LOAD_ACTION_SUCCESS](state, { payload }: LoadActionSuccessAction) {
    const action = normalizeAction(payload);
    const existingAction = state.find((a) => a.id === action.id) || {};

    return state
      .filter((a) => a.id !== action.id)
      .concat([{ ...existingAction, ...action }]);
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [LOAD_BOARD_SUCCESS](state, { payload }: any) {
    return [].concat(payload.actions).map((action) => normalizeAction(action));
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [LOAD_MEMBER_ACTIVITY_SUCCESS](state, { payload }: any) {
    return [].concat(payload.actions).map((action) => normalizeAction(action));
  },

  [ADD_CARD_COMMENT](state, { payload }: AddCardCommentAction) {
    return [normalizeAction(payload), ...state];
  },

  [DELETE_CARD_COMMENT](state, { payload }: DeleteCardCommentAction) {
    return state.filter((action) => action.id !== payload);
  },

  [SET_COMMENT_TEXT](state, { payload }: SetCommentTextAction) {
    const { id, data } = payload;
    const text = data && data.text;

    return state.map(
      (action) => (action.id !== id ? action : { ...action, text }),
    );
  },
});

export const addCardComment = ({
  idCard,
  text,
}: {
  idCard: string;
  text: string;
}): StandardThunkAction<Promise<AddCardCommentAction | void>> => {
  return async (dispatch, getState) => {
    try {
      const action = await API.client.rest.post<ActionResponse>(
        `cards/${idCard}/actions/comments`,
        { query: { text } },
      );

      trackUe({
        category: Noun.CARD,
        verb: Verb.ADDS,
        directObj: Noun.COMMENT,
        method: 'any method possible to add comment',
      });

      return dispatch(addCardCommentAction(action));
    } catch (err) {
      return Promise.reject(err);
    }
  };
};

export const deleteCardComment = (idAction: string): StandardThunkAction => {
  return async (dispatch, getState) => {
    try {
      await API.client.rest.del(`actions/${idAction}`);

      dispatch(deleteCardCommentAction(idAction));

      trackUe({
        category: Noun.CARD,
        verb: Verb.DELETES,
        directObj: Noun.COMMENT,
        method: 'any method possible to delete comment',
      });
    } catch (err) {
      console.error(err);
    }
  };
};

export const setCommentText = ({
  idAction,
  text,
}: {
  idAction: string;
  text: string;
}): StandardThunkAction => {
  return async (dispatch, getState) => {
    try {
      const action = await API.client.rest.put<ActionResponse>(
        `actions/${idAction}`,
      );

      dispatch(setCommentTextAction(action));
    } catch (err) {
      console.error(err);
    }
  };
};
